import { useInfiniteQuery } from '@tanstack/react-query';

import { LIST_GC_TIME_MS, LIST_STALE_TIME_MS, useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Template } from '@klareospace/pharos-lib-types';

import { createTemplateApi, templateKeys } from '../api';
import type { TemplateListResult } from '../api';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Number of templates fetched per page increment (not a hard total cap). */
const PAGE_SIZE = 20;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UsePaginatedTemplatesParams {
  /** When false the query is skipped entirely (lazy-load gate). Defaults to true. */
  enabled?: boolean;
  search?: string;
  type: string;
}

export interface UsePaginatedTemplatesReturn {
  error: Error | null;
  /** Whether at least one more page can be loaded. */
  hasMore: boolean;
  /** True during the initial page load. */
  isLoading: boolean;
  /** True while a subsequent page is being loaded. */
  isLoadingMore: boolean;
  /** Request the next page, if any. */
  loadMore: () => void;
  refetch: () => void;
  /** All fetched templates flattened across pages. */
  templates: Template[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Pages through the templates catalog using the backend's offset pagination.
 * Pages are flattened into a single list so the grid can grow as the user
 * scrolls. Changing `search` or `type` resets paging via the query key.
 */
export function usePaginatedTemplates({
  enabled = true,
  search,
  type,
}: UsePaginatedTemplatesParams): UsePaginatedTemplatesReturn {
  const client = useApiClient('deliveries');
  const api = createTemplateApi(client);

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } = useInfiniteQuery({
    enabled,
    gcTime: LIST_GC_TIME_MS,
    getNextPageParam: (lastPage: TemplateListResult) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => api.getTemplates({ page: pageParam, pageSize: PAGE_SIZE, search, type }),
    queryKey: templateKeys.list({ page: 0, pageSize: PAGE_SIZE, search, type }),
    staleTime: LIST_STALE_TIME_MS,
  });

  const templates = data?.pages.flatMap((page: TemplateListResult) => page.items) ?? [];

  function loadMore(): void {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }

  return {
    error: error as Error | null,
    hasMore: Boolean(hasNextPage),
    isLoading,
    isLoadingMore: isFetchingNextPage,
    loadMore,
    refetch,
    templates,
  };
}
